import { useState } from 'react';

export default function PilotTimeline() {
  const [activePhase, setActivePhase] = useState(0);

  const phases = [
    {
      phase: 1,
      title: 'Discovery & Assessment',
      duration: 'Week 1-2',
      icon: 'ri-search-eye-line',
      color: 'from-[#1e3a8a] to-[#2563eb]',
      description: 'Comprehensive evaluation of your current AI initiatives, data infrastructure, and organizational readiness.',
      deliverables: [
        'AI Maturity Assessment Report',
        'Data Landscape Analysis',
        'Stakeholder Alignment Workshop',
        'Quick Win Identification'
      ],
      metrics: { label: 'Assessment Score', value: '85+' }
    },
    {
      phase: 2,
      title: 'Strategy & Roadmap',
      duration: 'Week 3-4',
      icon: 'ri-route-line',
      color: 'from-[#2563eb] to-[#3b82f6]',
      description: 'Develop a tailored AI transformation roadmap with clear milestones, governance framework, and ROI projections.',
      deliverables: [
        'Enterprise AI Strategy Document',
        'Governance Framework Blueprint',
        'Technology Architecture Plan',
        'Investment & ROI Model'
      ],
      metrics: { label: 'ROI Projection', value: '250%+' }
    },
    {
      phase: 3,
      title: 'Pilot Development',
      duration: 'Week 5-8',
      icon: 'ri-flask-line',
      color: 'from-[#3b82f6] to-[#60a5fa]',
      description: 'Build and validate AI solutions in controlled environments with iterative feedback loops.',
      deliverables: [
        'Working AI Prototype',
        'Integration Test Results',
        'User Acceptance Criteria',
        'Performance Benchmarks'
      ],
      metrics: { label: 'Success Rate', value: '94%' }
    },
    {
      phase: 4,
      title: 'Production Scaling',
      duration: 'Week 9-12',
      icon: 'ri-rocket-2-line',
      color: 'from-[#60a5fa] to-[#93c5fd]',
      description: 'Deploy AI solutions at enterprise scale with full monitoring, security, and operational support.',
      deliverables: [
        'Production Deployment',
        'Monitoring Dashboard',
        'Runbook & Documentation',
        'Team Training Program'
      ],
      metrics: { label: 'Time to Value', value: '90 Days' }
    },
    {
      phase: 5,
      title: 'Optimization & Growth',
      duration: 'Ongoing',
      icon: 'ri-line-chart-line',
      color: 'from-[#1e3a8a] to-[#3b82f6]',
      description: 'Continuous improvement, model refinement, and expansion to new use cases across the enterprise.',
      deliverables: [
        'Performance Analytics',
        'Model Optimization Reports',
        'Expansion Roadmap',
        'Executive Dashboards'
      ],
      metrics: { label: 'Avg. Improvement', value: '40%/Qtr' }
    }
  ];

  return (
    <section className="py-24 bg-[#f0f4f8] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#1e3a8a]/10 text-[#1e3a8a] px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-git-branch-line"></i>
            Transformation Journey
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Your Path from <span className="text-[#1e3a8a]">Pilot to Production</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A proven 90-day framework that transforms experimental AI into enterprise-grade solutions.
          </p>
        </div>

        {/* Interactive Timeline */}
        <div className="relative">
          {/* Timeline Track */}
          <div className="hidden lg:block absolute top-[60px] left-0 right-0 h-1 bg-gray-200 rounded-full">
            <div 
              className="h-full bg-gradient-to-r from-[#1e3a8a] to-[#3b82f6] rounded-full transition-all duration-500"
              style={{ width: `${((activePhase + 1) / phases.length) * 100}%` }}
            ></div>
          </div>

          {/* Phase Nodes */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-12">
            {phases.map((phase, index) => (
              <button
                key={phase.phase}
                onClick={() => setActivePhase(index)}
                className={`relative flex flex-col items-center cursor-pointer group transition-all ${
                  activePhase === index ? 'scale-105' : 'opacity-70 hover:opacity-100'
                }`}
              >
                {/* Node Circle */}
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-all shadow-lg ${
                  activePhase === index 
                    ? `bg-gradient-to-br ${phase.color} text-white shadow-[#1e3a8a]/30` 
                    : 'bg-white border-2 border-gray-300 text-gray-400 group-hover:border-[#3b82f6]'
                }`}>
                  <i className={`${phase.icon} text-2xl`}></i>
                </div>
                
                {/* Phase Label */}
                <span className={`text-xs font-semibold uppercase tracking-wider mb-1 ${
                  activePhase === index ? 'text-[#1e3a8a]' : 'text-gray-400'
                }`}>
                  Phase {phase.phase}
                </span>
                <span className={`text-sm font-medium text-center ${
                  activePhase === index ? 'text-gray-900' : 'text-gray-600'
                }`}>
                  {phase.title}
                </span>
                <span className={`text-xs mt-1 ${
                  activePhase === index ? 'text-[#3b82f6]' : 'text-gray-400'
                }`}>
                  {phase.duration}
                </span>
              </button>
            ))}
          </div>

          {/* Active Phase Details */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8 md:p-10 shadow-xl">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Left: Description */}
              <div>
                <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${phases[activePhase].color} text-white px-4 py-2 rounded-full text-sm font-medium mb-4`}>
                  <i className={phases[activePhase].icon}></i>
                  Phase {phases[activePhase].phase}: {phases[activePhase].title}
                </div>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  {phases[activePhase].description}
                </p>
                
                {/* Key Metric */}
                <div className="inline-flex items-center gap-4 bg-[#1e3a8a]/5 rounded-xl p-4">
                  <div className="w-12 h-12 bg-[#1e3a8a] rounded-lg flex items-center justify-center">
                    <i className="ri-bar-chart-box-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{phases[activePhase].metrics.label}</p>
                    <p className="text-2xl font-bold text-[#1e3a8a]">{phases[activePhase].metrics.value}</p>
                  </div>
                </div>
              </div>

              {/* Right: Deliverables */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                  Key Deliverables
                </h4>
                <div className="space-y-3">
                  {phases[activePhase].deliverables.map((deliverable, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 bg-[#f0f4f8] rounded-lg p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="w-8 h-8 bg-[#3b82f6]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-[#3b82f6]"></i>
                      </div>
                      <span className="text-gray-700 font-medium">{deliverable}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={() => setActivePhase(Math.max(0, activePhase - 1))}
                disabled={activePhase === 0}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activePhase === 0 
                    ? 'text-gray-300 cursor-not-allowed' 
                    : 'text-[#1e3a8a] hover:bg-[#1e3a8a]/10'
                }`}
              >
                <i className="ri-arrow-left-line"></i>
                Previous Phase
              </button>
              
              <div className="flex items-center gap-2">
                {phases.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActivePhase(idx)}
                    className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                      activePhase === idx ? 'w-6 bg-[#1e3a8a]' : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={() => setActivePhase(Math.min(phases.length - 1, activePhase + 1))}
                disabled={activePhase === phases.length - 1}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activePhase === phases.length - 1 
                    ? 'text-gray-300 cursor-not-allowed' 
                    : 'text-[#1e3a8a] hover:bg-[#1e3a8a]/10'
                }`}
              >
                Next Phase
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <a 
            href="#conversion"
            className="inline-flex items-center gap-2 bg-[#1e3a8a] hover:bg-[#1e40af] text-white px-8 py-4 rounded-lg font-semibold transition-all transform hover:scale-105 shadow-lg cursor-pointer whitespace-nowrap"
          >
            Start Your Transformation Journey
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
